/**
 * S9: 个人中心 - 追加我的收藏 + 我的私信模块
 * 部署路径: /app/apps/main-site/frontend/src/pages/Dashboard.jsx
 * 改动说明: 在原有基础上增加 showcase 相关功能，原有代码不变
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CubeIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookmarkIcon,
  EnvelopeIcon,
  StarIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';
import api from '../services/api';

// ─── 我的收藏面板 ─────────────────────────────────────────────────────────────
function MyFavorites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get('/showcase/my/favorites?pageSize=8')
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUnfav = async (itemId) => {
    try {
      await api.delete(`/showcase/items/${itemId}/favorite`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      setTotal(t => t - 1);
    } catch {}
  };

  if (loading) return <div className="text-center text-gray-500 py-6 text-sm">加载中...</div>;

  if (items.length === 0) {
    return (
      <div className="text-center py-8">
        <BookmarkIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
        <p className="text-gray-400 text-sm">还没有收藏</p>
        <Link to="/showcases" className="text-blue-400 text-sm hover:underline mt-1 inline-block">去成果广场逛逛</Link>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        {items.map(item => (
          <div key={item.id} className="bg-gray-700/30 rounded-xl border border-gray-600/40 overflow-hidden group relative">
            <div className="h-24 bg-gradient-to-br from-blue-900/20 to-purple-900/20 overflow-hidden">
              {item.cover_image
                ? <img src={item.cover_image} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><StarIcon className="w-8 h-8 text-gray-600" /></div>
              }
            </div>
            <div className="p-2.5">
              <p className="text-white text-xs font-medium truncate">{item.project_name}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-gray-500 text-xs">
                  {new Date(item.favorited_at).toLocaleDateString()}
                </span>
                <button
                  onClick={() => handleUnfav(item.id)}
                  className="text-yellow-400 hover:text-gray-400 transition-colors"
                  title="取消收藏"
                >
                  <BookmarkSolid className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {total > 8 && (
        <Link to="/showcases" className="block text-center text-blue-400 text-xs mt-3 hover:underline">
          查看全部 {total} 个收藏
        </Link>
      )}
    </div>
  );
}

// ─── 我的私信面板 ─────────────────────────────────────────────────────────────
function MyMessages() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/showcase/my/messages')
      .then(r => setConversations(r.data.conversations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setMsgLoading(true);
    try {
      const r = await api.get(`/showcase/my/messages/${conv.item_id}`);
      setMessages(r.data.messages || []);
      // 更新未读数为0
      setConversations(prev => prev.map(c =>
        c.item_id === conv.item_id ? { ...c, unread_count: 0 } : c
      ));
    } catch {}
    finally { setMsgLoading(false); }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || sending || !activeConv) return;
    setSending(true);
    try {
      await api.post(`/showcase/my/messages/${activeConv.item_id}`, { content: reply });
      setReply('');
      // 重新加载消息
      const r = await api.get(`/showcase/my/messages/${activeConv.item_id}`);
      setMessages(r.data.messages || []);
    } catch (e) {
      alert(e.response?.data?.error || '发送失败');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-6 text-sm">加载中...</div>;

  if (!activeConv) {
    if (conversations.length === 0) {
      return (
        <div className="text-center py-8">
          <EnvelopeIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">暂无私信</p>
          <Link to="/showcases" className="text-blue-400 text-sm hover:underline mt-1 inline-block">去成果广场发现好项目</Link>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {conversations.map(conv => (
          <div
            key={conv.item_id}
            onClick={() => openConversation(conv)}
            className="flex items-center gap-3 p-3 bg-gray-700/30 hover:bg-gray-700/60 rounded-xl cursor-pointer transition-colors border border-gray-600/30"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-900/40 to-purple-900/40 rounded-lg overflow-hidden shrink-0">
              {conv.cover_image
                ? <img src={conv.cover_image} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><StarIcon className="w-5 h-5 text-gray-600" /></div>
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center">
                <p className="text-white text-sm font-medium truncate">{conv.project_name}</p>
                {parseInt(conv.unread_count) > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shrink-0 ml-1">
                    {conv.unread_count}
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs truncate mt-0.5">
                {conv.last_direction === 'tenant_to_user' ? '作者: ' : '我: '}
                {conv.last_content}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 对话详情视图
  return (
    <div>
      <button
        onClick={() => { setActiveConv(null); setMessages([]); }}
        className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm mb-3 transition-colors"
      >
        ← 返回列表
      </button>
      <p className="text-white text-sm font-medium mb-3">{activeConv.project_name}</p>

      {msgLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">加载中...</div>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1 mb-3">
          {messages.map(m => (
            <div key={m.id} className={`flex ${m.direction === 'user_to_tenant' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                m.direction === 'user_to_tenant'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-200'
              }`}>
                <p>{m.content}</p>
                <p className={`text-xs mt-1 ${m.direction === 'user_to_tenant' ? 'text-blue-200' : 'text-gray-500'}`}>
                  {new Date(m.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="回复..."
          className="flex-1 bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600 focus:outline-none focus:border-blue-500 placeholder-gray-500"
        />
        <button
          onClick={handleSend}
          disabled={sending || !reply.trim()}
          className="p-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-lg transition-colors"
        >
          <PaperAirplaneIcon className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}

// ─── 主组件 ───────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [expandedComplaint, setExpandedComplaint] = useState(null);
  const [complaintReplies, setComplaintReplies] = useState([]);
  const [activeTab, setActiveTab] = useState('complaints'); // 'complaints' | 'favorites' | 'messages'
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUser();
    fetchComplaints();
    fetchUnread();
    // 轮询未读数（30秒）
    const timer = setInterval(fetchUnread, 30000);
    return () => clearInterval(timer);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (err) {
      localStorage.removeItem('user_token');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnread = async () => {
    try {
      const r = await api.get('/showcase/my/unread-count');
      setUnreadCount(r.data.unread || 0);
    } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    navigate('/login');
  };

  const fetchComplaints = async () => {
    try {
      const response = await api.get('/auth/my-complaints');
      setComplaints(response.data.complaints || []);
    } catch {}
  };

  const toggleComplaint = async (complaint) => {
    if (expandedComplaint === complaint.id) {
      setExpandedComplaint(null);
      setComplaintReplies([]);
      return;
    }
    setExpandedComplaint(complaint.id);
    try {
      const response = await api.get(`/auth/my-complaints/${complaint.id}`);
      setComplaintReplies(response.data.replies || []);
    } catch {
      setComplaintReplies([]);
    }
  };

  const goToTenant = () => {
    const token = localStorage.getItem('user_token');
    window.location.href = `${import.meta.env.VITE_TENANT_BASE_URL || "https://www.noconb.com/app"}?token=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const membershipInfo = {
    free: { name: '免费版', color: 'text-gray-400', bg: 'bg-gray-500/20' },
    basic: { name: '基础版', color: 'text-blue-400', bg: 'bg-blue-500/20' },
    pro: { name: '专业版', color: 'text-purple-400', bg: 'bg-purple-500/20' },
    enterprise: { name: '企业版', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  };
  const currentPlan = membershipInfo[user?.membership || 'free'];

  // 套餐邮箱验证码配额
  const PLAN_EMAIL_QUOTA = { free: 20, basic: 5000, pro: 15000, enterprise: 50000 };
  const emailQuotaLimit = PLAN_EMAIL_QUOTA[user?.plan || user?.membership || 'free'] || 20;
  const emailQuotaUsed = user?.email_quota_used || 0;

  return (
    <div className="min-h-screen pt-20 pb-10">
      <div className="max-w-6xl mx-auto px-4">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">个人中心</h1>
            <p className="text-gray-400 mt-1">管理您的账号和项目</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>退出登录</span>
          </button>
        </div>

        {/* 顶部信息卡片 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 用户信息 */}
          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 gradient-bg rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user?.nickname?.charAt(0) || user?.email?.charAt(0) || '?'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{user?.nickname || '用户'}</h2>
                <p className="text-gray-400 text-sm">{user?.email}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">会员等级</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${currentPlan.bg} ${currentPlan.color}`}>
                  {currentPlan.name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">注册时间</span>
                <span className="text-white">{new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* 使用情况 */}
          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <CubeIcon className="w-5 h-5 mr-2 text-blue-400" />
              使用情况
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">项目数量</span>
                  <span className="text-white">{user?.project_count || 0} / {user?.max_projects || 1}</span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full gradient-bg" style={{ width: `${((user?.project_count || 0) / (user?.max_projects || 1)) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">存储空间</span>
                  <span className="text-white">{user?.storage_used_mb || 0} MB / 100 MB</span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full gradient-bg" style={{ width: `${((user?.storage_used_mb || 0) / 100) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">邮箱验证码</span>
                  <span className="text-white">{emailQuotaUsed} / {emailQuotaLimit.toLocaleString()}</span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full gradient-bg" style={{ width: `${Math.min((emailQuotaUsed / emailQuotaLimit) * 100, 100)}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* 会员服务 */}
          <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <CreditCardIcon className="w-5 h-5 mr-2 text-purple-400" />
              会员服务
            </h3>
            {!user?.membership || user.membership === 'free' ? (
              <div>
                <p className="text-gray-400 text-sm mb-4">升级会员解锁更多功能</p>
                <Link to="/pricing" className="block w-full py-3 gradient-bg text-white rounded-lg font-semibold text-center hover:opacity-90 transition-opacity">
                  查看升级方案
                </Link>
              </div>
            ) : (
              <div>
                <p className="text-gray-400 text-sm mb-2">当前方案: {currentPlan.name}</p>
                <p className="text-gray-400 text-sm mb-4">
                  到期时间: {user?.membership_expires_at ? new Date(user.membership_expires_at).toLocaleDateString() : '永久'}
                </p>
                <button className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors">续费</button>
              </div>
            )}
          </div>
        </div>

        {/* 进入工作台 */}
        <div className="mt-8 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-2xl p-8 border border-blue-500/30">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-2xl font-bold text-white mb-2">开始创建您的项目</h2>
              <p className="text-gray-300">进入开发工作台，使用可视化工具构建您的网站</p>
            </div>
            <button onClick={goToTenant} className="px-8 py-4 gradient-bg text-white rounded-xl font-semibold hover:opacity-90 transition-opacity glow">
              进入开发工作台，为安全须重新登录
            </button>
          </div>
        </div>

        {/* 底部 Tab 面板：反馈 / 收藏 / 私信 */}
        <div className="mt-8 bg-gray-800/50 rounded-2xl border border-gray-700 overflow-hidden">
          {/* Tab 导航 */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('complaints')}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors ${activeTab === 'complaints' ? 'text-white border-b-2 border-blue-500 bg-gray-700/30' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-400" />
              我的反馈
              {complaints.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-yellow-500/20 text-yellow-300 rounded-full">{complaints.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('favorites')}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors ${activeTab === 'favorites' ? 'text-white border-b-2 border-blue-500 bg-gray-700/30' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <BookmarkIcon className="w-4 h-4 text-yellow-400" />
              我的收藏
            </button>
            <button
              onClick={() => { setActiveTab('messages'); fetchUnread(); }}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors relative ${activeTab === 'messages' ? 'text-white border-b-2 border-blue-500 bg-gray-700/30' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <EnvelopeIcon className="w-4 h-4 text-blue-400" />
              我的私信
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Tab 内容 */}
          <div className="p-6">
            {activeTab === 'complaints' && (
              <>
                {complaints.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">暂无反馈记录</p>
                ) : (
                  <div className="space-y-3">
                    {complaints.map((c) => {
                      const statusStyles = {
                        pending:    { label: '待处理', bg: 'bg-red-500/20', text: 'text-red-300' },
                        processing: { label: '处理中', bg: 'bg-yellow-500/20', text: 'text-yellow-300' },
                        resolved:   { label: '已解决', bg: 'bg-green-500/20', text: 'text-green-300' },
                        closed:     { label: '已关闭', bg: 'bg-gray-500/20', text: 'text-gray-300' },
                      };
                      const st = statusStyles[c.status] || statusStyles.pending;
                      const isExpanded = expandedComplaint === c.id;
                      return (
                        <div key={c.id} className="bg-gray-700/30 rounded-xl border border-gray-600/50 overflow-hidden">
                          <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                            onClick={() => toggleComplaint(c)}
                          >
                            <div className="flex-1 min-w-0 mr-4">
                              <p className="text-white text-sm truncate">{c.content}</p>
                              <p className="text-gray-500 text-xs mt-1">{new Date(c.created_at).toLocaleString()}</p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {parseInt(c.reply_count) > 0 && (
                                <span className="flex items-center gap-1 text-blue-400 text-xs">
                                  <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />{c.reply_count}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                              {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t border-gray-600/50">
                              <div className="mt-3 bg-gray-800/50 rounded-lg p-3">
                                <p className="text-gray-300 text-sm whitespace-pre-wrap">{c.content}</p>
                              </div>
                              {complaintReplies.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <p className="text-xs text-gray-500">管理员回复：</p>
                                  {complaintReplies.map(r => (
                                    <div key={r.id} className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-blue-300 text-xs font-medium">{r.reply_by_name || 'Admin'}</span>
                                        <span className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleString()}</span>
                                      </div>
                                      <p className="text-gray-300 text-sm">{r.content}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {complaintReplies.length === 0 && parseInt(c.reply_count) === 0 && (
                                <p className="mt-3 text-gray-500 text-xs text-center">暂无回复</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {activeTab === 'favorites' && <MyFavorites />}
            {activeTab === 'messages' && <MyMessages />}
          </div>
        </div>
      </div>
    </div>
  );
}
